import cn from 'classnames';

import { evAddToFavorites } from 'events/favorites';
import { track } from 'apis/amethyst';
import { pluralize } from 'helpers';
import { buildRecosKey } from 'helpers/RecoUtils';

export function makeHeartClickHandler(heartEventData, clickHandlerDataAndActions) {
  const { addedFrom, heartEventName, unHeartEventName } = heartEventData;
  const { heartProduct, unHeartProduct, toggleHeartingLoginModal, trackEvent, isCustomer } = clickHandlerDataAndActions;
  return function({ styleId, productId, price }, isHearted) {
    if (isCustomer) {
      if (isHearted) {
        unHeartProduct({ itemId: styleId });
        trackEvent(unHeartEventName, styleId);
      } else {
        heartProduct({ itemId: styleId });
        trackEvent(heartEventName, styleId);
        track(() => ([
          evAddToFavorites, {
            styleId,
            productId,
            price,
            addedFrom
          }]));
      }
    } else {
      toggleHeartingLoginModal(true, styleId);
    }
  };
}

export function isProductHearted(showFavoriteHeart, hearts = [], styleId) {
  return showFavoriteHeart && styleId && hearts?.indexOf(styleId) > -1;
}

export function makeFavoriteHeartButton({
  cssHeartActive,
  cssHeartContainer,
  extraRecoStyle = null,
  handleHeartClick,
  hearts,
  heartsList,
  showFavoriteHeart,
  style,
  productId,
  testId = null }) {
  const { styleId } = style;
  if (heartsList && showFavoriteHeart) {
    const isHearted = isProductHearted(showFavoriteHeart, hearts, styleId);
    const count = (heartsList[styleId] > 0 || !isHearted) ? heartsList[styleId] : 1;
    return (
      <button
        type="button"
        className={cn(cssHeartContainer, { [cssHeartActive]: isHearted }, extraRecoStyle)}
        aria-label={`${isHearted ? 'Remove' : 'Add'} this item ${isHearted ? 'from' : 'to'} favorites. It has been favorited ${count} ${pluralize('time', count)}.`}
        onClick={handleHeartClick(style, productId)}
        data-test-id={testId}>
        {count}
      </button>
    );
  }
  return null;
}

export function makeHandleHeartButtonClick({
  hearts,
  onHeartClick,
  onCollectionToggle,
  productId,
  showFavoriteHeart,
  style }) {
  return e => {
    const { styleId, basePrice, salePrice } = style;
    const price = basePrice !== salePrice ? salePrice : basePrice;
    const isHearted = isProductHearted(showFavoriteHeart, hearts, styleId);

    // Prevent incorrect tracking events and going to product url
    e.stopPropagation();
    e.preventDefault();
    // Run hearting callback if present
    if (onHeartClick) {
      onCollectionToggle(!isHearted); // show collection if product has not been hearted prior to clicking "Heart"
      onHeartClick({
        styleId,
        productId,
        price
      }, isHearted);
    }
  };
}

// combines list object information with the items
export function makeCollectionInformationWithItems(lists, collections) {
  const collectionWithItems = [];
  if (lists?.length && collections?.length) {
    lists.forEach(list => {
      const correspondingCollection = collections.find(collection => list.listId === collection.listId);
      collectionWithItems.push({
        ...list, ...correspondingCollection
      });
    });
  }

  return collectionWithItems;
}

export function getHeartProps(heartProps, { addedFrom, heartEventName, unHeartEventName }) {
  const {
    hasHearting,
    isCustomer,
    heartProduct,
    hearts,
    products = {},
    toggleHeartingLoginModal,
    trackEvent,
    unHeartProduct
  } = heartProps || {};

  if (hasHearting) {
    const clickHandlerDataAndActions = { heartProduct, isCustomer, toggleHeartingLoginModal, trackEvent, unHeartProduct };
    return {
      showFavoriteHeart: true,
      onHeartClick: makeHeartClickHandler({ addedFrom, heartEventName, unHeartEventName }, clickHandlerDataAndActions),
      hearts,
      heartsList: products.heartsList
    };
  }

  return { showFavoriteHeart: false };
}

export function getHeartingStyleIdsForComponent(slotData, componentName, recommenderEntries = {}) {
  const results = [];
  Object.values(slotData || {}).forEach(slot => {
    if (slot.componentName === componentName) {
      switch (componentName) {
        case 'genericBrandTrending':
          results.push(...(slot.trending?.results || []));
          break;
        case 'productSearch':
          if (['melody', 'melody-curated', 'melody-grid'].includes(slot.style)) {
            results.push(...(slot.products || []));
          }
          break;
        case 'melodyGrid':
          results.push(...(slot.products || []));
          break;
        case 'recommender':
          const { janus = {} } = recommenderEntries;
          const janusEntry = janus[buildRecosKey(slot)];
          if (janusEntry) {
            const { recos = [] } = janusEntry;
            if (recos.length > 0) {
              results.push(...recos);
            }
          }
          break;
        default:
          if (recommenderEntries.length > 0) {
            results.push(...recommenderEntries);
          }
          break;
      }
    }
  });
  return results
    .map(result => ({ styleId: result.styleId }))
    .filter(result => !!result);
}
